/*
 * Transactions: the wallet as a statement (GET /transactions). Every movement
 * of money, newest first, with the balance after it:
 *
 *   credit  commission earned       pending for the hold, then withdrawable
 *   debit   commission reversed     the client's payment was refunded
 *   debit   withdrawal              the gross amount; GST and TDS come out of it
 *   credit  withdrawal returned     rejected by Wealthoak, or cancelled by the partner
 *
 * The balance is Pending + Available on the Wallet page. The server works out
 * every balance and total; this page only filters what is on screen, so it can
 * never disagree with the wallet. Ranges are calendar days in India, as on
 * Commissions.
 */

import { Fragment, useEffect, useMemo, useState, type ReactNode } from "react";
import { transactions, type Commission, type Ledger, type Transaction, type Withdrawal } from "../api/client";
import { useLoad } from "../api/useLoad";
import { useMe } from "../auth/AuthContext";
import { COMMISSION_STATE, EmptyState, LoadError, SETOFF_STATUS, SkeletonRows, Status, WITHDRAWAL_STATUS, type Tone } from "./bits";
import {
  fmtDate,
  fmtDateTime,
  fmtMonth,
  fmtStamp,
  fmtTime,
  fromPaise,
  inr,
  istMonth,
  plural,
  RANGES,
  rangeFor,
  rangeLabel,
  rateLabel,
  toPaise,
  todayIst,
  type RangeKey,
} from "./format";
import { ArrowLeftRight, CalendarRange, ChevronDown, Download, Info } from "./icons";
import { bankLine } from "./WithdrawDialog";
import "./transactions.css";

type Side = "all" | "credit" | "debit";

const SIDES: { key: Side; label: string }[] = [
  { key: "all", label: "All" },
  { key: "credit", label: "Credits" },
  { key: "debit", label: "Debits" },
];

export default function TransactionsWorkspace() {
  const me = useMe();
  const [rangeKey, setRangeKey] = useState<RangeKey>("fy");
  const [side, setSide] = useState<Side>("all");
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  // The range is state, not derived from rangeKey alone, so "today" is worked out again on every pick or retry.
  const [range, setRange] = useState(() => rangeFor("fy"));
  const ledger = useLoad(() => transactions.list(range), [range.from, range.to]);
  const data = ledger.data;
  // Stale data stays in `data` while another range loads or a reload fails: only trust it when the load is clean.
  const fresh = ledger.loading || ledger.error ? null : data;

  // Expanded rows belong to the ledger on screen.
  useEffect(() => setOpen(new Set()), [data]);

  function pickRange(key: RangeKey) {
    setRangeKey(key);
    setRange(rangeFor(key));
  }

  function retry() {
    setRange(rangeFor(rangeKey));
    ledger.reload();
  }

  const rows = useMemo(
    () => (data?.items ?? []).filter((t) => side === "all" || t.direction === side),
    [data, side],
  );

  // One group per month in India, in the order the rows arrive (newest first).
  const months = useMemo(() => {
    const groups: { month: string; items: Transaction[] }[] = [];
    for (const t of rows) {
      const month = istMonth(t.at);
      const last = groups[groups.length - 1];
      if (last?.month === month) last.items.push(t);
      else groups.push({ month, items: [t] });
    }
    return groups;
  }, [rows]);

  function toggle(id: string) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const empty =
    data && data.items.length === 0
      ? rangeKey === "all"
        ? {
            title: "No transactions yet",
            text: "Commission you earn and every withdrawal appear here, with your balance after each one.",
          }
        : { title: "Nothing in this range", text: "Try a longer range, or All." }
      : { title: side === "credit" ? "No credits in this range" : "No debits in this range", text: "Choose All to see every entry." };

  return (
    <>
      <section className="page-heading">
        <div>
          <p className="eyebrow">Transactions</p>
          <h1>Your wallet statement</h1>
          <p className="page-heading__sub">
            Every credit and debit in your commission wallet, with the balance after each one. The balance includes
            commission still inside the {me.hold_days}-day hold, so it matches Pending + Available on your Wallet.
          </p>
        </div>
        <div className="page-actions">
          <button
            className="button button--secondary"
            disabled={!fresh || fresh.items.length === 0}
            onClick={() => fresh && downloadCsv(fresh, range)}
          >
            <Download size={16} /> Download CSV
          </button>
        </div>
      </section>

      <div className="toolbar">
        <div className="tx-filters">
          <div className="range-tabs" role="group" aria-label="Date range">
            {RANGES.map((key) => (
              <button key={key} aria-pressed={rangeKey === key} onClick={() => pickRange(key)}>
                {rangeLabel(key)}
              </button>
            ))}
          </div>
          <div className="range-tabs" role="group" aria-label="Show">
            {SIDES.map((s) => (
              <button key={s.key} aria-pressed={side === s.key} onClick={() => setSide(s.key)}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <span className="toolbar__count tx-span">
          <CalendarRange size={14} />
          {range.from ? `${fmtDate(range.from)} – ${fmtDate(range.to)}` : "All time"}
          {data ? ` · ${plural(rows.length, "entry", "entries")}` : ""}
        </span>
      </div>

      <section className="stats" aria-label="Statement totals">
        <Tile
          label="Opening balance"
          value={tileValue(fresh, ledger.error, (l) => l.opening_balance)}
          sub={range.from ? `Before ${fmtDate(range.from)}` : "When you joined"}
        />
        <Tile
          label="Credits"
          value={tileValue(fresh, ledger.error, (l) => l.totals.credits)}
          sub="Commission earned, withdrawals returned"
          tone="is-green"
        />
        <Tile
          label="Debits"
          value={tileValue(fresh, ledger.error, (l) => l.totals.debits)}
          sub="Withdrawals and reversed commission"
        />
        <Tile
          label="Closing balance"
          value={tileValue(fresh, ledger.error, (l) => l.closing_balance)}
          sub="Pending + available today"
        />
      </section>

      <section className="card table-card" aria-label="Transactions" aria-busy={ledger.loading}>
        {ledger.error ? (
          <div className="table-card__state">
            <LoadError message={ledger.error} onRetry={retry} />
          </div>
        ) : !data ? (
          <SkeletonRows rows={6} label="Loading transactions" />
        ) : rows.length === 0 ? (
          <EmptyState icon={ArrowLeftRight} title={empty.title}>
            {empty.text}
          </EmptyState>
        ) : (
          <div className="table-scroll">
            <table className="data-table is-stacked tx-table">
              <caption className="sr-only">Wallet transactions, newest first, with the balance after each one</caption>
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Description</th>
                  <th scope="col">Reference</th>
                  <th scope="col" className="r">Credit</th>
                  <th scope="col" className="r">Debit</th>
                  <th scope="col" className="r">Balance</th>
                  <th scope="col">
                    <span className="sr-only">Details</span>
                  </th>
                </tr>
              </thead>
              {months.map((g) => (
                <tbody key={g.month}>
                  <tr className="tx-month">
                    <th scope="rowgroup" colSpan={7}>
                      {fmtMonth(g.month, "long")}
                    </th>
                  </tr>
                  {g.items.map((t) => (
                    <Entry key={t.id} t={t} open={open.has(t.id)} onToggle={() => toggle(t.id)} />
                  ))}
                </tbody>
              ))}
            </table>
          </div>
        )}
      </section>

      {data?.truncated && (
        <p className="notice page-foot">
          <Info size={14} />
          <span>
            This range has more than 500 entries, so only the latest 500 are listed. The balances and totals above
            cover the whole range. Choose a shorter range to see every entry.
          </span>
        </p>
      )}
      <p className="notice page-foot">
        <Info size={14} />
        <span>
          A withdrawal shows the full amount that left your wallet. GST and TDS come out of it before it reaches your
          bank: open the entry to see the split.
        </span>
      </p>
    </>
  );
}

/** undefined while loading (a skeleton), null when the load failed (a dash), else the figure. */
function tileValue(fresh: Ledger | null, error: string | null, pick: (l: Ledger) => string): string | null | undefined {
  return error ? null : fresh ? pick(fresh) : undefined;
}

function Tile({ label, value, sub, tone = "" }: { label: string; value: string | null | undefined; sub: string; tone?: string }) {
  return (
    <article className="card stat">
      <p className="eyebrow">{label}</p>
      {value === null ? (
        <strong>—</strong>
      ) : value !== undefined ? (
        <strong className={tone}>{inr(value)}</strong>
      ) : (
        <span className="skel skel--lg" style={{ marginTop: 10, width: "60%" }} />
      )}
      <small>{sub}</small>
    </article>
  );
}

/* ---------- one entry ---------- */

type Described = { title: string; status: string; tone: Tone; note: string | null; reference: string | null };

const utr = (w: Withdrawal) => (w.payout_reference ? `UTR ${w.payout_reference.replace(/^UTR\s*/i, "")}` : null);

/** A set-off: paid on the partner's own invoice from the wallet, not to the bank. */
const isSetoff = (w: Withdrawal) => w.method === "subscription";
/** A withdrawal's reference: the bank's UTR, or for a set-off the Zoho payment number. */
const payRef = (w: Withdrawal) => (isSetoff(w) ? (w.payout_reference ? `Payment ${w.payout_reference}` : null) : utr(w));
const statusOf = (w: Withdrawal) => (isSetoff(w) ? SETOFF_STATUS : WITHDRAWAL_STATUS)[w.status];

function describe(t: Transaction): Described {
  const c = t.commission;
  const w = t.withdrawal;
  const client = c?.client_name ?? "your client";
  switch (t.kind) {
    case "commission": {
      const st = c ? COMMISSION_STATE[c.state] : COMMISSION_STATE.available;
      return {
        title: `Commission · ${client}`,
        status: c?.state === "pending" ? `Pending until ${fmtDate(c.available_at)}` : st.label,
        tone: st.tone,
        note: c?.state === "reversed" ? `Reversed on ${fmtDate(c.reversed_at)}` : null,
        reference: c?.invoice_number ?? null,
      };
    }
    case "commission_reversal":
      return {
        title: `Commission reversed · ${client}`,
        status: "Reversed",
        tone: "red",
        note: c?.reversed_reason ?? null,
        reference: c?.invoice_number ?? null,
      };
    case "withdrawal": {
      const st = w ? statusOf(w) : WITHDRAWAL_STATUS.requested;
      if (w && isSetoff(w)) {
        // A set-off: paid on the partner's own Wealthoak invoice, not to the bank.
        return {
          title: `Paid on your invoice ${w.invoice_number ?? ""}`.trim(),
          status: st.label,
          tone: st.tone,
          note:
            w.status === "paid"
              ? `${inr(w.net_amount)} applied to your invoice after GST and TDS`
              : w.status === "rejected" || w.status === "cancelled"
                ? "Not applied. The amount came back to your wallet."
                : `${inr(w.net_amount)} goes to your invoice after GST and TDS`,
          reference: payRef(w),
        };
      }
      return {
        title: w?.account ? `Withdrawal to ••••${w.account.account_last4}` : "Withdrawal",
        status: st.label,
        tone: st.tone,
        note: !w
          ? null
          : w.status === "paid"
            ? `${inr(w.net_amount)} paid to your bank after GST and TDS`
            : w.status === "rejected" || w.status === "cancelled"
              ? "Not paid out. The amount came back to your wallet."
              : `${inr(w.net_amount)} goes to your bank after GST and TDS`,
        reference: w ? utr(w) : null,
      };
    }
    case "withdrawal_return":
      if (w && isSetoff(w)) {
        return {
          title: "Invoice payment returned to your wallet",
          status: SETOFF_STATUS[w.status].label,
          tone: "muted",
          note: w.reject_reason,
          reference: w.invoice_number,
        };
      }
      return {
        title: "Withdrawal returned to your wallet",
        status: w?.status === "cancelled" ? "Cancelled by you" : w?.status === "rejected" ? "Rejected by Wealthoak" : "Returned",
        tone: w?.status === "rejected" ? "red" : "muted",
        note: w?.status === "rejected" ? w.reject_reason : null,
        reference: null,
      };
  }
}

function Entry({ t, open, onToggle }: { t: Transaction; open: boolean; onToggle: () => void }) {
  const d = describe(t);
  const detailId = `tx-${t.id.replace(/[^a-z0-9]/gi, "")}`;
  return (
    <Fragment>
      <tr className={`tx-row is-${t.direction}${open ? " is-open" : ""}`}>
        <td data-label="Date" className="tx-date">
          <span>
            {fmtDate(t.at)}
            <small>{fmtTime(t.at)}</small>
          </span>
        </td>
        <td className="is-title tx-what">
          <strong>{d.title}</strong>
          <span className="tx-status">
            <Status tone={d.tone}>{d.status}</Status>
          </span>
          {d.note && <small>{d.note}</small>}
        </td>
        <td data-label="Reference" className="tx-ref">
          {d.reference ?? "—"}
        </td>
        <td data-label="Credit" className="r tx-credit">
          {t.direction === "credit" ? inr(t.amount) : null}
        </td>
        <td data-label="Debit" className="r tx-debit">
          {t.direction === "debit" ? inr(t.amount) : null}
        </td>
        <td data-label="Balance" className="r tx-balance">
          {inr(t.balance_after)}
        </td>
        <td className="tx-more">
          <button
            type="button"
            className="text-button tx-toggle"
            aria-expanded={open}
            aria-controls={open ? detailId : undefined}
            aria-label={`${open ? "Hide" : "Show"} details: ${d.title}, ${fmtDate(t.at)}`}
            onClick={onToggle}
          >
            {open ? "Hide" : "Details"} <ChevronDown size={14} />
          </button>
        </td>
      </tr>
      {open && (
        <tr className="tx-detail" id={detailId}>
          <td colSpan={7} className="tx-detail__cell">
            {t.commission ? (
              <CommissionDetail c={t.commission} />
            ) : t.withdrawal ? (
              <WithdrawalDetail w={t.withdrawal} returnedAt={t.kind === "withdrawal_return" ? t.at : null} />
            ) : null}
          </td>
        </tr>
      )}
    </Fragment>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function CommissionDetail({ c }: { c: Commission }) {
  return (
    <dl className="kv tx-kv">
      <Row label="Client">{c.client_name ?? "—"}</Row>
      <Row label="Invoice">{c.invoice_number ?? "—"}</Row>
      <Row label="Client paid (incl. GST)">{inr(c.payment_amount)}</Row>
      <Row label="Fee (excl. GST)">{inr(c.base_amount)}</Row>
      <Row label="Your rate">{rateLabel(c.rate)}</Row>
      <Row label="Commission">
        <strong>{inr(c.amount)}</strong>
      </Row>
      <Row label="Earned">{fmtDateTime(c.earned_at)}</Row>
      {c.state === "reversed" ? (
        <Row label="Reversed">
          {fmtDateTime(c.reversed_at)}
          {c.reversed_reason && <small>{c.reversed_reason}</small>}
        </Row>
      ) : (
        <Row label={c.state === "pending" ? "Withdrawable from" : "Withdrawable since"}>{fmtDate(c.available_at)}</Row>
      )}
    </dl>
  );
}

function WithdrawalDetail({ w, returnedAt }: { w: Withdrawal; returnedAt: string | null }) {
  const setoff = isSetoff(w);
  const st = statusOf(w);
  const settled = w.status === "rejected" || w.status === "cancelled";
  return (
    <dl className="kv tx-kv">
      <Row label="From your wallet">{inr(w.amount)}</Row>
      <Row label={`GST (${rateLabel(w.gst_rate)}, included)`}>− {inr(w.gst_amount)}</Row>
      <Row label={`TDS (${rateLabel(w.tds_rate)})`}>− {inr(w.tds_amount)}</Row>
      <Row
        label={
          setoff
            ? w.status === "paid"
              ? "Applied to your invoice"
              : settled
                ? "Net, not applied"
                : "To be applied to your invoice"
            : w.status === "paid"
              ? "Paid to your bank"
              : settled
                ? "Net, not paid out"
                : "To be paid to your bank"
        }
      >
        <strong>{inr(w.net_amount)}</strong>
      </Row>
      {w.account ? (
        <Row label="Bank account">
          {w.account.account_holder}
          <small>{bankLine(w.account)}</small>
        </Row>
      ) : (
        <Row label="Invoice">{w.invoice_number ?? "—"}</Row>
      )}
      <Row label="Status">
        <Status tone={st.tone}>{st.label}</Status>
      </Row>
      <Row label="Requested">{fmtDateTime(w.requested_at)}</Row>
      {!setoff && (w.status === "approved" || w.status === "paid") && (
        <Row label="Approved">{fmtDateTime(w.decided_at)}</Row>
      )}
      {w.status === "paid" && (
        <Row label={setoff ? "Applied" : "Paid"}>
          {fmtDateTime(w.paid_at)}
          {payRef(w) && <small className="mono">{payRef(w)}</small>}
        </Row>
      )}
      {w.status === "rejected" && (
        <Row label={setoff ? "Returned" : "Rejected"}>
          {fmtDateTime(w.decided_at)}
          {w.reject_reason && <small>{w.reject_reason}</small>}
        </Row>
      )}
      {w.status === "cancelled" && <Row label="Cancelled by you">{fmtDateTime(w.cancelled_at ?? w.decided_at)}</Row>}
      {settled && <Row label="Back in your wallet">{fmtDateTime(returnedAt ?? w.cancelled_at ?? w.decided_at)}</Row>}
    </dl>
  );
}

/* ---------- CSV ---------- */

/** A spreadsheet cell. Text that starts like a formula is quoted so it stays text. */
function csvCell(value: string): string {
  const safe = /^-?\d+(\.\d+)?$/.test(value) || !/^[=+\-@\t\r]/.test(value) ? value : `'${value}`;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/**
 * The range as a CSV statement, oldest first, between an opening and a closing
 * balance line. Money is the API's plain 2 dp figure, so a spreadsheet reads it
 * as a number. When the list was cut short, it opens on the balance before the
 * oldest entry it has.
 */
function downloadCsv(data: Ledger, range: { from?: string; to?: string }) {
  const items = [...data.items].reverse();
  const first = items[0];
  const opening = first
    ? fromPaise(toPaise(first.balance_after) - (first.direction === "credit" ? 1 : -1) * toPaise(first.amount))
    : data.opening_balance;

  const lines: string[][] = [
    ["Date (IST)", "Entry", "Details", "Reference", "Status", "Credit", "Debit", "Balance"],
    ["", "Opening balance", "", "", "", "", "", opening],
    ...items.map((t) => {
      const d = describe(t);
      return [
        fmtStamp(t.at),
        d.title,
        d.note ?? "",
        d.reference ?? "",
        d.status,
        t.direction === "credit" ? t.amount : "",
        t.direction === "debit" ? t.amount : "",
        t.balance_after,
      ];
    }),
    ["", "Closing balance", "", "", "", "", "", data.closing_balance],
  ];

  const csv = lines.map((line) => line.map(csvCell).join(",")).join("\r\n");
  // The byte-order mark makes Excel read the file as UTF-8, so names in any script come through intact.
  const url = URL.createObjectURL(new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `wealthoak-statement-${range.from ?? "start"}-to-${range.to ?? todayIst()}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Not at once: some browsers start reading the blob after the click handler returns.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
